/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, toJS } from "mobx";
import { getViewMode } from "selectors/viewModeSelectors";
import { docViewModes, rhpDropinIds } from "constants/enums";
import { pagesInView, thumbnailsInView } from "selectors/pagedViewSelectors";
import { apiFactory } from "api/external/ApiFactory";
import { CLEAR_QUEUE_EVENTS } from "api/events/clearAJSQueueEvents";
import { publicApiNames } from "publicApis/constants";
import { overlayEvents } from "overlays/OverlayEvents";
import { providers } from "dc-core";

class PageThemeSwitcherStore {
  @observable isDarkPageThemeEnabled = false;
  @observable isDarkPageThemeSwitchDisabled = false;
  @observable colorPallete = null;
  @observable docViewTheme = "auto";
  @observable userPrefThemeCallback = null;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    this.docApi = docApi || this.docApi;
    this.stores = stores || this.stores;
  }

  @action
  init() {
    this.isDarkPageThemeEnabled = false;
    this.colorPallete = null;
    this.userPrefThemeCallback = null;
    this.isDarkPageThemeSwitchDisabled = false;
    this.customReadingThemesSet = [];
  }

  @action.bound
  setDarkPageTheme(isDarkPageThemeEnabled) {
    this.isDarkPageThemeEnabled = isDarkPageThemeEnabled;
    this.updateColorPallete();
  }

  @action.bound
  setDocViewTheme(docViewTheme) {
    this.docViewTheme = docViewTheme;
  }

  @action.bound
  setDarkPageThemeSwitchDisabled(isDarkPageThemeSwitchDisabled) {
    this.isDarkPageThemeSwitchDisabled = isDarkPageThemeSwitchDisabled;
    this.updateColorPallete();
  }

  @action.bound
  setUserPrefThemeCallback(callback) {
    this.userPrefThemeCallback = callback;
  }

  @action.bound
  setCustomReadingThemesSet(customReadingThemesSet) {
    this.customReadingThemesSet = customReadingThemesSet;
  }

  @action.bound
  async setColorPallete(newColorPallete) {
    if (JSON.stringify(newColorPallete) !== JSON.stringify(this.colorPallete)) {
      this.colorPallete = newColorPallete;
      await this.reloadView();
    }
  }

  getcolorPallete() {
    return toJS(this.colorPallete);
  }

  updateColorPallete() {
    if (this.isDarkPageThemeEnabled && !this.isDarkPageThemeSwitchDisabled) {
      this.setColorPallete({ text: [255, 255, 255], background: [29, 29, 29], colorSpace: "rgb" });
    } else {
      this.setColorPallete(null);
    }
  }

  async __shouldReloadThumbnails() {
    const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
    if (rhpApis && rhpApis[publicApiNames.RHP]) {
      const activeDropin = await rhpApis[publicApiNames.RHP].getConfig();
      if (activeDropin && activeDropin.dropinId && activeDropin.dropinId === rhpDropinIds.THUMBNAILS) {
        return true;
      }
    }
    return false;
  }

  async reloadView() {
    if (!this.__shouldReloadPages()) return;

    this.reloadPages();
    const shouldReloadThumbnails = await this.__shouldReloadThumbnails();
    shouldReloadThumbnails ? this.reloadThumbnails() : this.stores.documentStore.unloadAllThumbnails();
  }

  __shouldReloadPages() {
    const viewMode = getViewMode(this.stores);
    return ![docViewModes.NON_PDF, docViewModes.NO_PREVIEW, docViewModes.UNKNOWN].includes(viewMode);
  }

  reloadPages() {
    const colorPallete = this.getcolorPallete();
    this.stores.documentStore.clearLoadPageRequests({ evtName: CLEAR_QUEUE_EVENTS.PAGE_THEME_CHANGE, colorPallete });
    this.stores.documentStore.reloadPages(pagesInView(this.stores));
    overlayEvents.trigger("documentViewThemeChange", colorPallete);
  }

  reloadThumbnails() {
    this.stores.documentStore.reloadThumbnails(thumbnailsInView(this.stores));
  }

  changeAppTheme(theme) {
    providers.theme().then(themeProvider => {
      themeProvider.changeTheme(theme);
    });
  }
}

export { PageThemeSwitcherStore };
